// pages/overview.js — Overview page.
//
// Owns the `data-page="overview"` section. The canonical landing page
// after Home V2 was retired (2026-05-15). Renders hero copy + 6 stat
// tiles, the chat activity timeline, top-active-chats panel, and the
// recent-events panel with decision-type pill filters.
//
// Two data paths, same renderers:
//
//   - Local-daemon mode: bootstrap.js's renderAll() loop polls the
//     daemon and writes into the shared `state` object (state.leases,
//     state.leasesAll, state.events, state.analytics). The existing
//     renderers below read from those globals.
//
//   - Cloud mode (dashboard.agentcollision.com): bootstrap.js's
//     daemon polls don't fire — no daemon at same-origin. This module
//     stands up its OWN fetch loop against the coordinator's
//     /v1/teams/* endpoints and writes the results into the same
//     `state` globals so the renderers don't need to fork. SSE feeds
//     state.events live; a 5s tile poll feeds state.analytics + 6 tile
//     values; a 5s lease poll feeds state.leasesAll/state.leases for
//     the timeline + top-chats aggregation.
//
// Time-range selector (24h/7d/30d) drives the 5 windowed tiles, the
// counts response, the leases-recent fetch, and the events backfill.
// "Files being edited" + "Collisions prevented" tiles ignore it
// (real-time and 30d-fixed respectively). Persisted to ?since= so
// reload + share-link both keep the selection.

import { state } from "../state.js";
import { refreshActivityLines } from "../activity-lines.js";
import { patchHTML, animateValue, $, loadingHTML } from "../dom.js";
import { isLocalDaemonDown, isTeamUnreachable } from "../team-status.js";
import { escapeHTML, ago } from "../util.js";
import {
  purposeToPath,
  inferChatLabel,
  parseAgentID,
  worktreeFromPurpose,
  toolIcon,
  eventRowHTML,
} from "../format.js";
import { aggregateChats, resolveChatMember, resolveChatRepo } from "../chats.js";
import { isCloudMode, usesCloudShapedEndpoints } from "../ui.js";
import { api, isCoordinatorCooling } from "../api.js";
import { withBuFilter } from "../bu-switcher.js";
import { scheduleDynamic, isSSEEnabled } from "../tier-policy.js";
import { getWhoami } from "../whoami.js";
import { decryptRepoForRows, decryptEventRow, buildDeviceLabelMap } from "../decrypt-helpers.js";
import { decryptHeartbeat } from "../crypto.js";
import { linkToEvents, windowLabel } from "../event-links.js";
import { agentsState } from "../page-state.js";
import { navigate } from "../router.js";
import { renderAgentsPage } from "./agents.js";

// Coordinator base URL for cloud-mode SSE. EventSource can't attach
// Authorization headers — the request goes to the absolute coordinator
// URL with a query-string stream-token minted via /v1/stream/grant.
const COORDINATOR =
  (typeof window !== "undefined" &&
    window.AC_CONFIG &&
    window.AC_CONFIG.API_BASE) ||
  "";

// ---- time range ----

const VALID_RANGES = new Set(["24h", "7d", "30d"]);

function currentRange() {
  const p = new URLSearchParams(window.location.search);
  const r = p.get("since") || "";
  return VALID_RANGES.has(r) ? r : "24h";
}

function setRange(r) {
  if (!VALID_RANGES.has(r)) return;
  const p = new URLSearchParams(window.location.search);
  p.set("since", r);
  history.replaceState(
    null,
    "",
    window.location.pathname + "?" + p.toString() + window.location.hash,
  );
  refreshActivityLines();
}

// ---- scope view (mine | team) ----
//
// "mine" scopes the tiles + timeline to the signed-in user; "team"
// shows the whole team and relabels the timeline by member + repo.
// Mirrors the range control's URL-persistence so refresh + share-link
// work. Only meaningful in cloud mode for team admins (see
// bindScopeControl). Reads/writes state.overviewView too so widgets.js
// can branch without re-parsing the URL.

const VALID_VIEWS = new Set(["mine", "team"]);

function currentView() {
  const p = new URLSearchParams(window.location.search);
  const v = p.get("view") || "";
  return VALID_VIEWS.has(v) ? v : "mine";
}

function setView(v) {
  if (!VALID_VIEWS.has(v)) return;
  state.overviewView = v;
  const p = new URLSearchParams(window.location.search);
  p.set("view", v);
  history.replaceState(
    null,
    "",
    window.location.pathname + "?" + p.toString() + window.location.hash,
  );
}

// isAdmin — the Team scope is gated to team admins. Mirrors team-page.js.
// Solo / local-daemon has no your_role; the control is .cloud-only so it
// never renders there anyway, but this keeps the JS honest.
function isAdmin() {
  return !!(state.teamInfo && state.teamInfo.your_role === "admin");
}

// ---- cloud fetch loop ----

// All cloud-mode runtime state lives here. Kept module-local so the
// shared `state` object stays a clean cache populated FROM these
// fetches rather than the source of truth.
const cloudState = {
  // PR A: dynamic scheduleDynamic timers replace the static setInterval
  // pair below. The legacy fields stay defined so a partial rollout
  // (or a future explicit setInterval addition) doesn't crash stop().
  countsCancel: null,  // scheduleDynamic cancel for /decision-counts
  leasesCancel: null,  // scheduleDynamic cancel for /v1/teams/leases
  countsTimer: null,   // legacy setInterval (kept for partial-rollout safety)
  leasesTimer: null,   // legacy setInterval
  es: null,            // EventSource for live event stream
  reconnectTimer: null,
  reconnectDelayMs: 1000,
  // Monotonic counter — async openCloudStream() invocations check
  // this at each await boundary and bail if a newer call superseded
  // them. Same pattern as the (now-deleted) home.js used.
  streamGeneration: 0,
  // Last coordination_events.id we've delivered to state.events. Sent
  // as a URL param on manual reconnect so the worker can replay only
  // newer events instead of the full since=1h window (PR #134). The
  // browser also sends EventSource's native Last-Event-ID header on
  // auto-reconnect; the worker treats either source as authoritative.
  lastEventId: null,
  // Actor-scope param the most recent counts/leases fetch was built
  // with. scopeActorParam() depends on state.teamInfo (admin check)
  // and the whoami cache, both of which load asynchronously — so the
  // initial fetches usually fire UNFILTERED (whole-team data) and a
  // later poll silently re-fires FILTERED, replacing the tiles with
  // the narrower scope. maybeRescope() (run from renderOverview)
  // compares this against the live scopeActorParam() and refetches
  // the moment they diverge, so the page converges on the correct
  // scope on the next render pass instead of flapping at the next
  // poll interval (the "Overview shows data, then goes blank on
  // revisit" bug).
  lastScopeParam: undefined,
  rescopeInFlight: false,
};

// Decision-type pill state (mirrors state.overviewPill, but we re-read
// the DOM to stay decoupled from the legacy bridge).
function activePill() {
  return state.overviewPill || "all";
}

// scopeActorParam returns "&actor_subject=<sub>" when the Overview
// should narrow to the signed-in user, else "". It narrows ONLY when
// the admin-gated scope toggle is present AND set to "mine" — so a
// non-admin (who has no toggle) keeps the pre-existing whole-team/BU
// view, unchanged. "team" view, anonymous/legacy session, or non-admin
// → "" (no narrowing). The coordinator applies the filter (team bird's-
// eye backend PR).
function scopeActor() {
  if (!isCloudMode() || !isAdmin()) return "";
  if (currentView() !== "mine") return "";
  const who = (getWhoami() && getWhoami().identity) || "";
  if (!who || who === "anonymous") return "";
  return who;
}

function scopeActorParam() {
  const who = scopeActor();
  return who ? `&actor_subject=${encodeURIComponent(who)}` : "";
}

// linkHeroTiles makes each headline number open the list it counts, with
// the same window and "My chats" scope the number used.
function linkHeroTiles(tiles) {
  const range = currentRange();
  const win = windowLabel(range);
  const actor = scopeActor();
  const cell = (id) => {
    const el = document.getElementById(id);
    return el ? el.closest(".hero-stat-cell") : null;
  };
  linkToEvents(cell("hero-throughput"), { types: ["lease.acquired"], since: range, actor, label: "Edits coordinated · " + win });
  linkToEvents(cell("hero-event-rate"), { since: range, actor, label: "All events · " + win });
  linkToEvents(cell("hero-prevented"), { types: ["lease.blocked"], since: "30d", actor, label: "Collisions prevented · last 30 days" });
  linkToEvents(cell("hero-collision-rate"), { types: ["lease.blocked"], since: range, actor, label: "Blocked edits · " + win });
  const chats = cell("hero-chats");
  if (chats) {
    chats.classList.add("kpi-link");
    chats.setAttribute("role", "link");
    chats.setAttribute("tabindex", "0");
    chats._activeChats = { ids: tiles.agents_window || [], total: tiles.agent_count_window || 0, label: "Chats active in the " + win };
  }
}

function openActiveChats(cell) {
  const a = cell._activeChats;
  if (!a) return;
  agentsState.only = { ids: new Set(a.ids), total: a.total, label: a.label };
  agentsState.showEnded = true;
  navigate("agents");
  renderAgentsPage();
}

function openHeldFiles() {
  state.leaseFilterState = "held";
  document.querySelectorAll("[data-lease-state]").forEach((c) => c.classList.toggle("active", c.dataset.leaseState === "held"));
  navigate("leases");
}

function bindHeroLinks() {
  const grid = document.getElementById("hero-grid");
  if (!grid || grid.dataset.kpiBound) return;
  grid.dataset.kpiBound = "1";
  const held = document.getElementById("hero-active");
  const heldCell = held && held.closest(".hero-stat-cell");
  if (heldCell) {
    heldCell.classList.add("kpi-link");
    heldCell.setAttribute("role", "link");
    heldCell.setAttribute("tabindex", "0");
  }
  const act = (e) => {
    const cell = e.target.closest(".hero-stat-cell");
    if (!cell) return;
    if (cell === heldCell) openHeldFiles();
    else if (cell._activeChats) openActiveChats(cell);
  };
  grid.addEventListener("click", act);
  grid.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      act(e);
    }
  });
}

// Returns true on success so the initial-fetch retry loop in start()
// can detect when to stop retrying. Internal failures still leave
// previous state in place (next tick retries).
async function fetchCountsOnce() {
  const range = currentRange();
  const scopeParam = scopeActorParam();
  cloudState.lastScopeParam = scopeParam;
  try {
    const data = await api(
      withBuFilter(`/v1/teams/events/decision-counts?since=${range}${scopeParam}`),
    );
    // Mirror the local analytics shape so updateHero() can read
    // state.analytics.totals.blocked uniformly across modes.
    state.analytics = state.analytics || {};
    state.analytics.totals = state.analytics.totals || {};
    state.analytics.totals.blocked =
      (data.tiles && data.tiles.collisions_prevented_30d) || 0;
    cloudState.lastCounts = data;
    state.connected = true;
    renderCloudTiles(data);
    return true;
  } catch (_err) {
    // 401/network — leave previous values; next tick retries.
    return false;
  }
}

async function fetchLeasesOnce() {
  const range = currentRange();
  try {
    // Phase 4b (v0.38.0): when the Leases page "mine only" toggle
    // is on AND we have a verified whoami subject cached, narrow
    // the query to the current user's leases. Coordinator-side
    // filter lives on `actor_subject` (migration 039). Without the
    // toggle on OR without a subject (anonymous/legacy session)
    // the URL stays unchanged — backward-compatible with
    // pre-v0.38.0 callers.
    // Scope the timeline to the user in "mine" view (scopeActorParam),
    // OR to the legacy Leases-page "mine only" toggle — either narrows
    // on actor_subject. In "team" view both are off and we get the
    // whole team. Don't double-append if both would fire.
    let url = `/v1/teams/leases?since=${range}`;
    const scopeParam = scopeActorParam();
    cloudState.lastScopeParam = scopeParam;
    if (scopeParam) {
      url += scopeParam;
    } else if (state.leaseFilterMineOnly) {
      const who = (getWhoami() && getWhoami().identity) || "";
      if (who && who !== "anonymous") {
        url += `&actor_subject=${encodeURIComponent(who)}`;
      }
    }
    const data = await api(withBuFilter(url));
    // Cloud's `held` already has the PascalCase shape the renderers
    // expect. `recent` gives us acquired events to feed the timeline
    // chart through the same code path the daemon uses.
    //
    // These rows skip decryptLeaseRow (they arrive pre-reshaped), so
    // their RepoHint blob is still encrypted — decrypt it into .Repo
    // here or the Leases page REPO column falls back to the
    // root-stripped resource and shows "--". Decrypt once; both
    // state.leases and state.leasesAll derive from data.held.
    const held = await decryptRepoForRows(data.held || []);
    state.leases = held.filter((l) => l.State === "held");
    // NB: decryptRepoForRows now also recovers Resource from DisplayHint, so
    // held[].Resource is a real path (not the hashed wire value) when the key
    // is loaded — see decrypt-helpers.js. The recent rows below carry the same
    // blob under payload_hint; we surface it as DisplayHint so the SAME decrypt
    // pass recovers their path too.
    // leasesAll feeds aggregateChats. We synthesize "released" lease
    // shapes from the recent acquired events.
    const heldByLeaseID = new Map(
      held.map((l) => [l.LeaseID, l]),
    );
    // Recent rows are lease.acquired EVENTS — their recoverable path lives in
    // payload_hint as a JSON blob (NOT the two-line lease display_hint format),
    // so decrypt them via decryptEventRow (which parses the JSON and resolves
    // the path from purpose/last_file_path/resource) and reuse its Resource.
    // Was previously dropped (Resource: e.resource hashed, DisplayHint: ""),
    // which left recent-only chats' paths unreadable on the cloud poller.
    // decryptEventRow drops lease_id, so we keep the raw event (`src`) alongside
    // to preserve it.
    const recentRaw = (data.recent || []).filter(
      (e) => !heldByLeaseID.has(e.lease_id),
    );
    const recentDecrypted = await Promise.all(
      recentRaw.map(async (e) => ({ src: e, dec: await decryptEventRow(e) })),
    );
    const recentShaped = recentDecrypted
      .map(({ src, dec }) => ({
        LeaseID: src.lease_id || "",
        AgentID: dec.AgentID || src.agent_id,
        Resource: dec.Resource || src.resource || "",
        State: "released",
        AcquiredAt: src.occurred_at,
        ExpiresAt: src.occurred_at,
        LastHeartbeatAt: src.occurred_at,
        DisplayHint: "",
        Purpose: dec.Purpose || "",
        // ReleasedAt blank: the daemon doesn't emit lease.released into
        // coordination_events on every path, so only the acquired side is
        // known.
        ReleasedAt: "",
      }));
    state.leasesAll = [...held, ...recentShaped];
    return true;
  } catch (_err) {
    /* swallow — next tick retries */
    return false;
  }
}

// fetchMembersOnce + fetchHeartbeatsOnce hydrate the data the Team view
// needs to resolve a chat's agent_id → a team member:
//   members → state.people (laptop_id → email via memberName())
//   heartbeats → state.heartbeats (agent_id → laptop_id, and fuels the
//     timeline for chats that haven't acquired a lease yet)
// bootstrap.js fetches these for the local-daemon path; the cloud
// Overview loop didn't. Only needed in Team view, but cheap (≤500 rows)
// and also improves Mine. Same decrypt calls bootstrap.js makes.
async function fetchMembersOnce() {
  try {
    const members = await api("/v1/teams/members");
    state.members = members.members || [];
    state.people = members.people || [];
    return true;
  } catch (_err) {
    return false;
  }
}

async function fetchHeartbeatsOnce() {
  try {
    const hb = await api("/v1/teams/heartbeats");
    const raw = hb.heartbeats || [];
    const decrypted = await Promise.all(raw.map(decryptHeartbeat));
    state.heartbeats = decrypted;
    state.deviceLabels = buildDeviceLabelMap(decrypted, state.deviceLabels);
    return true;
  } catch (_err) {
    return false;
  }
}

function renderCloudTiles(data) {
  if (!data || !data.tiles) return;
  const t = data.tiles;
  bindHeroLinks();
  linkHeroTiles(t);
  // The legacy updateHero() handles writes_in_progress + active chats
  // via state.leases.length + aggregateChats(). It also handles
  // throughput via state.leasesAll. We add the 3 tiles updateHero
  // doesn't know about: collision rate, event rate, edits coordinated.
  // (Collisions-prevented goes through state.analytics.totals.blocked
  // which we set in fetchCountsOnce; updateHero already binds it.)
  setTile(
    "hero-collision-rate",
    t.conflict_rate != null ? (t.conflict_rate * 100).toFixed(1) + "%" : "—",
  );
  setTile(
    "hero-event-rate",
    t.event_rate_per_min != null ? t.event_rate_per_min.toFixed(2) : "—",
  );
  // edits coordinated is bound in updateHero via state.leasesAll
  // counting; we let it run. The cloud counts also expose
  // tiles.edits_coordinated as a direct number — we prefer the
  // direct number for cloud mode because the 1h window logic in
  // updateHero is hard-coded.
  const throughputEl = document.getElementById("hero-throughput");
  if (throughputEl) animateValue(throughputEl, t.edits_coordinated || 0);
  // Active-chats tile: cloud counts gives us agent_count_window over
  // the selected range. updateHero would have set it to the 1h-window
  // aggregateChats() count, which is fine for local mode but doesn't
  // honor the time-range selector in cloud mode.
  const chatsEl = document.getElementById("hero-chats");
  if (chatsEl) animateValue(chatsEl, t.agent_count_window || 0);
  // Collision-rate delta + chats delta show the current window for
  // disambiguation.
  const rangeLabel = labelForRange(currentRange());
  const crD = document.getElementById("hero-collision-rate-delta");
  if (crD) crD.textContent = rangeLabel;
  const chatsD = document.getElementById("hero-chats-delta");
  if (chatsD) chatsD.textContent = `in ${rangeLabel}`;
  const throughputD = document.getElementById("hero-throughput-delta");
  if (throughputD) throughputD.textContent = `in ${rangeLabel}`;
  // Update pill counts from by_event_type.
  const by = data.by_event_type || {};
  const total = Object.values(by).reduce((s, n) => s + n, 0);
  document.querySelectorAll("[data-count-for]").forEach((el) => {
    const key = el.getAttribute("data-count-for");
    if (key === "all") el.textContent = total;
    else el.textContent = by[key] != null ? by[key] : "·";
  });
}

function labelForRange(r) {
  if (r === "7d") return "last 7 days";
  if (r === "30d") return "last 30 days";
  return "last 24h";
}

function setTile(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

// ---- cloud SSE ----

async function openCloudStream() {
  closeCloudStream();
  cloudState.streamGeneration += 1;
  const myGen = cloudState.streamGeneration;

  let grant;
  try {
    grant = await api("/v1/stream/grant", { method: "POST" });
  } catch (_err) {
    if (myGen !== cloudState.streamGeneration) return;
    scheduleCloudReconnect();
    return;
  }
  if (myGen !== cloudState.streamGeneration) return;
  if (!grant || !grant.stream_token) {
    scheduleCloudReconnect();
    return;
  }
  const params = new URLSearchParams();
  params.set("token", grant.stream_token);
  // since=1h for SSE backfill — the tile counts use the selected
  // range; the stream itself shows the latest hour of activity so the
  // pane has content immediately on tab focus.
  params.set("since", "1h");
  // Last-Event-ID resume via URL param (PR #134). EventSource native
  // auto-reconnect attaches the Last-Event-ID *request header*, but
  // our manual close()+new EventSource() reconnect path (scheduled
  // after onerror or after a stream-grant rotation) goes through a
  // fresh EventSource constructor that won't carry it. The worker
  // checks both the header AND this `last_event_id` URL param so
  // manual reconnects also get clean resume semantics. The id is
  // tracked on cloudState by the onFrame handler.
  if (typeof cloudState.lastEventId === "number") {
    params.set("last_event_id", String(cloudState.lastEventId));
  }
  const url = COORDINATOR + "/v1/teams/events/stream?" + params.toString();

  if (myGen !== cloudState.streamGeneration) return;
  const es = new EventSource(url);
  cloudState.es = es;
  es.onopen = () => {
    cloudState.reconnectDelayMs = 1000;
  };
  es.onerror = () => {
    closeCloudStream();
    scheduleCloudReconnect();
  };

  const onFrame = (ev) => {
    try {
      const event = JSON.parse(ev.data);
      // Prepend into state.events with the legacy `_isNew` flag so
      // renderOverviewEvents picks up the highlight pulse.
      event._isNew = true;
      // Dedupe by id (PR #134): the Last-Event-ID resume path can
      // emit a stream.gap signal followed by a ring-replay AND the
      // gap-catch-up D1 fetch — there's overlap. dedupe collapses
      // them. event.id is the coordination_events.id (number); fall
      // back to a composite key when absent.
      const evId = typeof event.id === "number" ? event.id : null;
      const existing = state.events || [];
      const duplicate = evId !== null && existing.some((e) => e && e.id === evId);
      if (!duplicate) {
        state.events = [event, ...existing].slice(0, 200);
      }
      // Track the highest id we've seen for manual-reconnect resume
      // (PR #134). EventSource's native auto-reconnect handles this
      // via the Last-Event-ID header, but our explicit close()+new
      // EventSource() path needs to send it via URL param. Take the
      // max so a stale frame arriving after a newer one doesn't
      // rewind us.
      if (evId !== null && (cloudState.lastEventId === null || evId > cloudState.lastEventId)) {
        cloudState.lastEventId = evId;
      }
      // Re-render only if Overview is the active page; otherwise we
      // just keep the buffer warm.
      if (state.activePage === "overview") renderOverviewEvents();
    } catch (_err) {
      /* swallow */
    }
  };

  // stream.gap handler (PR #134 scaling pass): the worker emits
  // this synthetic event on SSE reconnect when our Last-Event-ID
  // is older than the ring's oldest entry — meaning we missed
  // events while disconnected that the in-memory ring has already
  // evicted. Fire an explicit /v1/teams/events catch-up against D1
  // to backfill the gap; new events flowing through onFrame
  // dedupe against this fetch via the id-based check above.
  const onGap = async (ev) => {
    // Generation guard (PR D arc sweep finding #1, HIGH): the
    // gap handler closes over myGen from this openCloudStream
    // invocation. If the user has since downgraded (Team→Free)
    // and tickHandler bumped streamGeneration to invalidate this
    // stream, the gap-catch-up fetch shouldn't fire — it would
    // pull events into state and confuse the polling-only Free
    // experience. Bail out early.
    if (myGen !== cloudState.streamGeneration) return;
    try {
      const gapInfo = JSON.parse(ev.data);
      const lastEventId = gapInfo.id;
      // Fetch the events newer than lastEventId. We use a generous
      // limit; D1 caps response size anyway. The "since" query
      // accepts time durations or absolute timestamps; we don't
      // have a wall-clock for lastEventId so fall back to 1h to
      // re-cover the SSE since= window. The id-dedupe below makes
      // over-fetching cheap.
      const resp = await api(withBuFilter("/v1/teams/events?since=1h&limit=500"));
      // Re-check after the await — a downgrade could land while
      // the fetch is in flight.
      if (myGen !== cloudState.streamGeneration) return;
      const events = (resp && resp.events) || [];
      // Merge: prepend any event whose id > lastEventId AND not
      // already in state.events. Sort by occurred_at descending
      // after merge so render order is stable.
      const known = new Set((state.events || []).map((e) => e && e.id).filter((id) => typeof id === "number"));
      const newOnes = events.filter((e) => {
        if (typeof e.id !== "number") return false;
        if (e.id <= lastEventId) return false;
        return !known.has(e.id);
      });
      if (newOnes.length > 0) {
        state.events = [...newOnes, ...(state.events || [])]
          .sort((a, b) => new Date(b.occurred_at || 0).getTime() - new Date(a.occurred_at || 0).getTime())
          .slice(0, 200);
        if (state.activePage === "overview") renderOverviewEvents();
      }
    } catch (_err) {
      // Best-effort: if the catch-up fails the stream keeps flowing
      // and the user might miss a few events. Next reconnect tries
      // again. Not worth a toast — most likely cause is a brief
      // network blip that's already over by the next render.
    }
  };

  // SSE's `event:` line steers each frame to its named listener —
  // there's no automatic fallthrough to `message`. Register every
  // event_type the daemon's teamsync.go pushEvents whitelist emits.
  // Keep in sync with internal/teamsync/teamsync.go.
  const STREAM_EVENT_TYPES = [
    "lease.acquired",
    "lease.blocked",
    "lease.expired",
    "lease.released",
    "lease.preempted",
    "lease.revoked",
    "policy.fired",
    "write.unmanaged_conflict",
    "write.unmanaged_acknowledged",
    "agent.registered",
    "agent.connected",
    "agent.disconnected",
    "team.switched",
  ];
  es.addEventListener("message", onFrame);
  STREAM_EVENT_TYPES.forEach((t) => es.addEventListener(t, onFrame));
  es.addEventListener("stream.gap", onGap);
}

function closeCloudStream() {
  if (cloudState.es) {
    try {
      cloudState.es.close();
    } catch (_err) {
      /* ignore */
    }
  }
  cloudState.es = null;
}

function scheduleCloudReconnect() {
  if (cloudState.reconnectTimer) clearTimeout(cloudState.reconnectTimer);
  const delay = cloudState.reconnectDelayMs;
  cloudState.reconnectTimer = setTimeout(() => {
    cloudState.reconnectTimer = null;
    if (cloudState.running) openCloudStream();
  }, delay);
  cloudState.reconnectDelayMs = Math.min(cloudState.reconnectDelayMs * 2, 30000);
}

// ---- legacy renderers (read from shared `state`) ----

export function updateHero() {
  const chats = aggregateChats();
  const oneHourAgo = Date.now() - 60 * 60 * 1000;
  const throughput = state.leasesAll.filter(
    (l) => new Date(l.AcquiredAt).getTime() >= oneHourAgo,
  ).length;

  animateValue($("hero-active"), state.leases.length);
  // When the dashboard hits cloud-shape endpoints (cloud-pages mode
  // OR local-daemon mode in v0.31.2+), renderCloudTiles updates
  // hero-chats + hero-throughput directly from the decision-counts
  // response (so the time-range selector works AND so local-daemon
  // mode populates the tiles at all — pre-v0.31.9 the fallback ran
  // but state.leasesAll was never hydrated, leaving the tiles blank).
  // Fall back to the legacy local-mode calculations only when neither
  // path is available.
  if (!usesCloudShapedEndpoints()) {
    animateValue($("hero-chats"), chats.length);
    animateValue($("hero-throughput"), throughput);
  }
  // Collisions-prevented = lease.blocked count from the 30d analytics
  // window. Each blocked event is one moment AC kept two chats from
  // racing on the same file. Local mode: state.analytics.totals.blocked
  // comes from /v1/analytics; cloud mode: same field, populated by
  // fetchCountsOnce above (tiles.collisions_prevented_30d).
  const prevented =
    (state.analytics &&
      state.analytics.totals &&
      state.analytics.totals.blocked) ||
    0;
  animateValue($("hero-prevented"), prevented);

  // First-run welcome state: zero activity AND zero historical events.
  //
  // v0.31.7: the welcome banner copy is cloud-team-onboarding-shaped
  // ("Invite teammates", "Open a Claude Code session"). It's actively
  // misleading in local-daemon mode — the user already installed
  // the daemon (they're hitting the daemon-served dashboard) and the
  // "Welcome to your team dashboard" framing doesn't apply to solo.
  // Suppress the banner entirely when window.AC_CONFIG.DEPLOY_MODE
  // is local-daemon; show the actual hero tiles even on empty state.
  const localDaemonMode =
    window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE === "local-daemon";
  const isFirstRun =
    !localDaemonMode &&
    state.connected &&
    state.leases.length === 0 &&
    chats.length === 0 &&
    throughput === 0 &&
    state.events.length === 0 &&
    state.leasesAll.length === 0;
  const wb = $("welcome-banner");
  const hg = $("hero-grid");
  if (isFirstRun) {
    if (wb) wb.classList.add("visible");
    if (hg) hg.style.display = "none";
  } else {
    if (wb) wb.classList.remove("visible");
    if (hg) hg.style.display = "";
  }

  // No state.loading guard here. The headline chain below handles
  // transient empty-data states via the hasLiveActivity check plus
  // local-daemon-mode special-casing. The static HTML ships its
  // own skeleton placeholder that disappears the first time we
  // paint real text.
  const everConnected = (state.members || []).some((m) => m.last_seen);
  const anyOnline = (state.members || []).some((m) => m.is_online);
  let headline, detail;
  // Count the chats actually holding the files, not every chat active
  // this hour.
  const inFlightHeadline = () => {
    const n = state.leases.length;
    const holders = new Set(state.leases.map((l) => l.AgentID)).size;
    return `${n} file${n === 1 ? "" : "s"} being edited by ${holders} chat${holders === 1 ? "" : "s"}`;
  };
  // Pulse the hero headline only when something is genuinely live —
  // writes in flight, or chats that have acquired leases recently.
  // Idle / disconnected / no-daemon states stay static so the motion
  // signals "things are happening" rather than just decoration.
  let isLive = false;
  // v0.31.7: in local-daemon mode, "connected" means the local
  // daemon is reachable (we're served by it). The "Coordinator
  // unreachable" headline is cloud-mode-specific; don't show it
  // in local-daemon mode. fetchCountsOnce sets state.connected=true
  // when /v1/teams/events/decision-counts returns 200, which in
  // solo mode hits the local handler — so this should already work
  // most of the time. The guard below covers the brief window
  // before the first counts fetch completes.
  // Activity-derived signal that the daemon is clearly alive — even
  // when state.members is empty or stale (a common transient state
  // when the coordinator hasn't seen a fresh heartbeat yet, or when
  // the daemon's first push hasn't landed). If any of these are
  // non-zero, we KNOW there's a working daemon somewhere, regardless
  // of what the members roster says — so the "No daemon yet" path
  // would be lying.
  const hasLiveActivity =
    state.leases.length > 0 || chats.length > 0 || throughput > 0;

  if (isLocalDaemonDown()) {
    headline = "Not connected";
    detail =
      "AgentCollision on this computer isn't answering. It may be restarting after an update; this page reconnects on its own.";
  } else if (!state.connected && !localDaemonMode) {
    headline = "Coordinator unreachable";
    detail =
      "Cannot reach the team coordinator. Check your network connection.";
  } else if (isCloudMode() && hasLiveActivity) {
    // If there's live activity (writes in flight, chats acquiring
    // leases, throughput > 0), there's a working daemon — even if
    // /v1/teams/members hasn't populated yet or is reporting stale
    // rows with last_seen: null. Pre-fix the hero would flap between
    // "12 writes in flight" and "No daemon yet" on every refresh
    // tick because the coordinator's members table lagged the
    // live SSE event stream. Trust activity over roster.
    headline = inFlightHeadline();
    detail = `Team coordination active. ${throughput} total leases in the last hour.`;
    isLive = true;
  } else if (!everConnected && !hasLiveActivity && !localDaemonMode) {
    // "No daemon yet" only fires when: no member ever checked in,
    // there's no live activity, AND we're not in local-daemon mode
    // (where the dashboard is served by the daemon — its existence
    // is proven by being able to load this page). The earlier shape
    // tripped this branch on transient empty-roster polls, causing
    // the hero to flap between "12 writes in flight" and "No daemon
    // yet" every refresh tick.
    headline = "No daemon yet";
    detail =
      'Install AgentCollision on a computer to start coordinating. <a href="#" onclick="navigate(\'machines\'); return false;" style="color: var(--accent); font-weight: 600;">Machines &rarr; My machines</a> has a one-line install command.';
  } else if (!anyOnline && !hasLiveActivity) {
    headline = "All daemons offline";
    detail =
      "Your team's daemons haven't checked in recently. Make sure AgentCollision is running on at least one machine.";
  } else if (
    state.leases.length === 0 &&
    chats.length === 0 &&
    throughput === 0
  ) {
    headline = "Idle";
    detail =
      "AgentCollision is running, but no chat is editing files right now. Start editing in a Claude Code chat to see activity here.";
  } else if (
    state.leases.length === 0 &&
    (chats.length > 0 || throughput > 0)
  ) {
    headline = `${chats.length} chat${chats.length === 1 ? "" : "s"} active recently`;
    detail = `${throughput} lease${throughput === 1 ? "" : "s"} acquired in the last hour. No chat is editing a file right now.`;
    isLive = chats.length > 0 || throughput > 0;
  } else {
    headline = inFlightHeadline();
    detail = `Team coordination active. ${throughput} total leases in the last hour.`;
    isLive = true;
  }
  const hl = $("hero-headline");
  if (hl) {
    // Use a span sibling for the dot so it can pulse independently of the
    // text node; rewriting innerHTML on every tick would thrash the DOM,
    // so only touch it when the text actually changes.
    const textSpan = hl.querySelector(".hero-pulse-text");
    if (!textSpan) {
      hl.innerHTML =
        '<span class="hero-pulse-dot" aria-hidden="true"></span>' +
        '<span class="hero-pulse-text"></span>';
    }
    const t = hl.querySelector(".hero-pulse-text");
    if (t && t.textContent !== headline) t.textContent = headline;
    hl.classList.toggle("is-live", isLive);
  }
  patchHTML($("hero-detail"), detail);
}

export function renderOverviewTopChats() {
  const chats = aggregateChats().slice(0, 3);
  const wrap = $("overview-top-chats");
  if (!wrap) return;
  // Rows win whoever fetched them. With none, "No active chats" waits for
  // the boot fetch: state.loading always clears when it settles (fetchAll's
  // finally), so this can't stay on Loading.
  if (chats.length === 0 && state.loading) {
    patchHTML(wrap, loadingHTML("chats"));
    return;
  }
  if (chats.length === 0) {
    patchHTML(
      wrap,
      `
        <div class="empty">
          <div class="empty-title">No active chats</div>
          <div class="empty-detail">No chat sessions have acquired a lease in the last hour.</div>
        </div>`,
    );
    return;
  }
  patchHTML(
    wrap,
    chats
      .map((c, _i) => {
        const recentPath = c.leases[0]
          ? purposeToPath(c.leases[0].Purpose)
          : "";
        const label = inferChatLabel(c.agentID, recentPath, c);
        const agent = parseAgentID(c.agentID);
        // Team view: prefer the resolved member name + repo as the
        // headline so the bird's-eye list reads as "alice · acme/web".
        // Falls back to the inferred label when unattributable.
        const teamView = state.overviewView === "team";
        const teamMember = teamView ? resolveChatMember(c.agentID) : "";
        const teamRepo = teamView ? resolveChatRepo(c.agentID) : "";
        const displayName = teamMember || label.name;
        const wt = c.leases[0] ? worktreeFromPurpose(c.leases[0].Purpose) : "";
        const fileNames = Array.from(c.files.keys()).slice(0, 3).join(", ");
        return `
        <div style="padding: var(--s3) 0; border-bottom: 1px solid var(--border); cursor: pointer; transition: background var(--transition-fast); border-radius: var(--r-md); padding: var(--s3) var(--s2);" class="overview-chat-row" data-page-link="chats" onmouseover="this.style.background='var(--bg-panel-hover)'" onmouseout="this.style.background=''">
          <div style="display: flex; align-items: center; gap: var(--s3); margin-bottom: 4px;">
            <span class="chat-status-dot ${c.held > 0 ? "" : c.browsing ? "browsing" : "idle"}"></span>
            <span>${toolIcon(agent.tool)}</span>
            <strong>${escapeHTML(displayName)}</strong>
            <span style="font-family: var(--font-mono); font-size: 11px; color: var(--info); font-weight: 600;">${escapeHTML(agent.tool)}</span>
            <span style="color: var(--text-tertiary); font-size: 11px; margin-left: auto; font-variant-numeric: tabular-nums;">${escapeHTML(ago(new Date(c.mostRecentTs).toISOString()))}</span>
          </div>
          <div style="font-size: 12px; color: var(--text-secondary); padding-left: 22px; display: flex; gap: var(--s3); flex-wrap: wrap;">
            ${teamRepo ? `<span style="font-family: var(--font-mono); color: var(--accent);">${escapeHTML(teamRepo)}</span><span>&middot;</span>` : ""}
            <span>${c.totalLeases} edit${c.totalLeases === 1 ? "" : "s"}</span>
            <span>&middot;</span>
            <span>${c.files.size} file${c.files.size === 1 ? "" : "s"}</span>
            ${wt ? `<span>&middot;</span><span style="font-family: var(--font-mono); color: var(--text-tertiary);">${escapeHTML(wt)}</span>` : ""}
          </div>
          ${fileNames ? `<div style="font-size: 11px; color: var(--text-tertiary); padding-left: 22px; margin-top: 2px; font-family: var(--font-mono);">${escapeHTML(fileNames)}</div>` : ""}
        </div>`;
      })
      .join(""),
  );
}

export function renderOverviewEvents() {
  const wrap = $("overview-events");
  if (!wrap) return;
  const filterType = activePill();
  const filtered =
    filterType === "all"
      ? state.events
      : state.events.filter(
          (e) => (e.event_type || e.EventType) === filterType,
        );
  const list = filtered.slice(0, 8);
  if (state.events.length === 0 && !state.eventsLoaded && !isTeamUnreachable()) {
    patchHTML(wrap, loadingHTML("events"));
    return;
  }
  if (list.length === 0) {
    const msg =
      filterType === "all"
        ? "Events appear here as agents acquire and release leases."
        : `No "${filterType.replace("lease.", "")}" events in the recent stream. Try "All" or wait for new activity.`;
    patchHTML(
      wrap,
      `
        <div class="empty">
          <div class="empty-title">${filterType === "all" ? "No events yet" : "No matches"}</div>
          <div class="empty-detail">${msg}</div>
        </div>`,
    );
    return;
  }
  patchHTML(
    wrap,
    list
      .map((e, i) => {
        const isNew = e._isNew;
        e._isNew = false;
        return eventRowHTML(e, isNew, i);
      })
      .join(""),
  );
}

// updateScopeSubtitles writes the "· your chats" / "· all team chats"
// hint next to the Activity + Top-chats panel titles, so the timeline's
// scope is legible. Only shows in cloud mode for admins (who can flip
// the toggle); empty otherwise. Cheap text-only update.
function updateScopeSubtitles() {
  const show = isCloudMode() && isAdmin();
  const label = !show
    ? ""
    : currentView() === "team"
      ? " · all team chats"
      : " · your chats";
  const a = document.getElementById("overview-activity-scope");
  const t = document.getElementById("overview-topchats-scope");
  if (a) a.textContent = label;
  if (t) t.textContent = label;
}

function renderOverview() {
  // Re-bind the scope toggle every render: state.teamInfo (gating it)
  // loads asynchronously and may arrive after start(). bindScopeControl
  // is idempotent — it attaches its click listener once and otherwise
  // just refreshes visibility/active state.
  bindScopeControl();
  updateScopeSubtitles();
  renderOverviewTopChats();
  renderOverviewEvents();
  updateHero();
  maybeRescope();
}

// maybeRescope refetches counts + leases when the actor scope the last
// fetch used (cloudState.lastScopeParam) no longer matches the live
// scopeActorParam(). The scope's inputs — state.teamInfo (admin gate)
// and the whoami cache — load asynchronously, so the initial fetches
// usually fire UNFILTERED and the correct filter only becomes
// computable afterwards. Hooked into renderOverview (which runs after
// every fetch completion and on dashboard:tick), so the page converges
// on the right scope on the next render pass instead of waiting for
// the 30-60s scheduled poll. Guarded to one rescope in flight; the
// fetchers record lastScopeParam at call start even on failure, so a
// failed rescope can't loop — the scheduled poll carries the retry.
function maybeRescope() {
  if (
    !usesCloudShapedEndpoints() ||
    !cloudState.running ||
    cloudState.rescopeInFlight ||
    cloudState.lastScopeParam === undefined ||
    scopeActorParam() === cloudState.lastScopeParam
  ) {
    return;
  }
  cloudState.rescopeInFlight = true;
  Promise.all([fetchCountsOnce(), fetchLeasesOnce()])
    .catch(() => {})
    .then(() => {
      cloudState.rescopeInFlight = false;
      if (state.activePage === "overview") renderOverview();
    });
}

// ---- decision pill wiring ----

function bindDecisionPills() {
  const host = document.getElementById("overview-decision-pills");
  if (!host) return;
  host.querySelectorAll(".decision-pill").forEach((btn) => {
    btn.addEventListener("click", () => {
      const pill = btn.getAttribute("data-pill") || "all";
      state.overviewPill = pill;
      host.querySelectorAll(".decision-pill").forEach((b) => {
        const isActive = b === btn;
        b.classList.toggle("active", isActive);
        // Inline-style toggle to match the existing styled pills.
        if (isActive) {
          b.style.borderColor = "var(--accent)";
          b.style.background = "var(--accent-bg)";
          b.style.color = "var(--accent)";
          b.style.fontWeight = "600";
        } else {
          b.style.borderColor = "var(--border)";
          b.style.background = "var(--bg-elevated)";
          b.style.color = "var(--text-secondary)";
          b.style.fontWeight = "";
        }
      });
      renderOverviewEvents();
    });
  });
}

// ---- time-range control wiring ----

function bindRangeControl() {
  const host = document.getElementById("overview-range-control");
  if (!host) return;
  const current = currentRange();
  host.querySelectorAll(".segmented-button").forEach((btn) => {
    const r = btn.getAttribute("data-range");
    btn.classList.toggle("active", r === current);
    btn.addEventListener("click", async () => {
      const newRange = btn.getAttribute("data-range");
      if (!newRange || newRange === currentRange()) return;
      setRange(newRange);
      host.querySelectorAll(".segmented-button").forEach((b) => {
        b.classList.toggle("active", b === btn);
      });
      // Refresh windowed data immediately so the user doesn't wait for
      // the next 5s tick. v0.31.9: also fires in local-daemon mode since
      // the daemon serves the same /v1/teams/* endpoints. We AWAIT and
      // retry once on failure: a single transient 502 on the
      // daemon→coordinator hop used to be swallowed silently, leaving
      // the KPI tiles frozen on the OLD window so the date control
      // looked dead. (The daemon also retries the hop now; this is the
      // client-side belt-and-suspenders.)
      if (usesCloudShapedEndpoints()) {
        const [okC, okL] = await Promise.all([fetchCountsOnce(), fetchLeasesOnce()]);
        if (!okC || !okL) {
          await new Promise((res) => setTimeout(res, 400));
          if (!okC) await fetchCountsOnce();
          if (!okL) await fetchLeasesOnce();
        }
      }
      // Hero text needs a re-render even in local mode because
      // updateHero reads the throughput off state.leasesAll regardless
      // of mode; the tile labels also include the range label.
      renderOverview();
    });
  });
}

// bindScopeControl wires the My-chats/Team toggle. Admin + cloud only:
// the control is .cloud-only (hidden in local-daemon by CSS) and we
// additionally hide it for non-admins, leaving the default "mine"
// behavior. Switching view changes the actor_subject scope on the next
// fetch (tiles + timeline) AND flips widgets.js into member/repo labels
// via state.overviewView.
//
// IDEMPOTENT + re-callable: state.teamInfo (which isAdmin reads) is
// loaded by bootstrap's fetchAll, which races overview's start(). If
// start() bound the toggle before teamInfo arrived, an admin would
// never get it. So renderOverview() re-invokes this every tick; the
// click listener attaches exactly once (guarded by a dataset flag) and
// every call just refreshes visibility + active state.
function bindScopeControl() {
  const host = document.getElementById("overview-scope-control");
  if (!host) return;
  // Gate: hosted + admin only. The .cloud-only class already hides it
  // in local-daemon; this also hides it from non-admin team members.
  if (!isCloudMode() || !isAdmin()) {
    host.style.display = "none";
    // Force mine so a non-admin never sits in a stale ?view=team.
    if (currentView() !== "mine") setView("mine");
    return;
  }
  host.style.display = "";
  state.overviewView = currentView();
  const current = currentView();
  // Reflect active state every call (cheap, idempotent).
  host.querySelectorAll(".segmented-button").forEach((btn) => {
    btn.classList.toggle("active", btn.getAttribute("data-scope-view") === current);
  });
  // If we just became an admin in team view (e.g. teamInfo arrived after
  // start() and the URL carried ?view=team), make sure the member +
  // heartbeat roster is hydrated so the relabel resolves. Fetch once;
  // guarded so re-renders don't refetch every tick.
  if (current === "team" && !host.dataset.teamHydrated) {
    host.dataset.teamHydrated = "1";
    Promise.all([fetchMembersOnce(), fetchHeartbeatsOnce()]).then(() => renderOverview());
  }
  // Attach the click handler ONCE — re-calls must not stack listeners.
  if (host.dataset.scopeWired === "1") return;
  host.dataset.scopeWired = "1";
  host.querySelectorAll(".segmented-button").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const newView = btn.getAttribute("data-scope-view");
      if (!newView || newView === currentView()) return;
      setView(newView);
      host.querySelectorAll(".segmented-button").forEach((b) => {
        b.classList.toggle("active", b === btn);
      });
      // Team view needs the member roster + heartbeats to resolve
      // chats → members; fetch them on the way in (idempotent, cached).
      if (newView === "team") {
        await Promise.all([fetchMembersOnce(), fetchHeartbeatsOnce()]);
      }
      // Refetch the scoped tiles + timeline immediately so the toggle
      // feels instant (same await-and-retry shape as bindRangeControl).
      const [okC, okL] = await Promise.all([fetchCountsOnce(), fetchLeasesOnce()]);
      if (!okC || !okL) {
        await new Promise((res) => setTimeout(res, 400));
        if (!okC) await fetchCountsOnce();
        if (!okL) await fetchLeasesOnce();
      }
      renderOverview();
    });
  });
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  bindRangeControl();
  bindScopeControl();
  bindDecisionPills();
  if (usesCloudShapedEndpoints()) {
    cloudState.running = true;
    state.overviewOwnsLeases = isCloudMode();
    // Initial fetches. Runs in both cloud-pages and local-daemon
    // modes — the daemon serves the same /v1/teams/* endpoints in
    // solo mode (stubs) or via proxy when team-connected.
    //
    // Bounded retry: in cloud mode, start() can fire before Supabase
    // session restoration completes. fetchCountsOnce / fetchLeasesOnce
    // catch their own errors internally (returning false), so the
    // retry just loops on the boolean. Worst-case ~10.5s before we
    // give up on first paint; scheduled poll then takes over at the
    // tier's cadence.
    const retryFetch = async (fn) => {
      const delays = [0, 200, 500, 1000, 2000, 2000, 2000, 2000];
      for (let i = 0; i < delays.length; i++) {
        if (delays[i] > 0) await new Promise((r) => setTimeout(r, delays[i]));
        // Stop early if the coordinator circuit breaker is open (it just
        // rate-limited us). Spinning the remaining retries would just waste
        // ~10s of timers for nothing; the breaker fast-fails them anyway. The
        // scheduled poll picks back up once the cooldown elapses.
        if (isCoordinatorCooling()) return;
        const ok = await fn();
        if (ok) {
          renderOverview();
          return;
        }
      }
    };
    retryFetch(fetchCountsOnce);
    retryFetch(fetchLeasesOnce);
    // Team view (incl. a refresh landing on ?view=team) needs the
    // member roster + heartbeats to resolve chats → members. Fetch
    // once on mount when in team view; the toggle handler refetches on
    // switch. Best-effort — the timeline falls back to anonymous labels
    // if these fail. Admins only reach team view (bindScopeControl).
    if (currentView() === "team" && isAdmin()) {
      fetchMembersOnce();
      fetchHeartbeatsOnce();
    }
    // SSE only for tiers that have it enabled (PR A Free-tier gating).
    // Free tier (DB tier='solo') skips this — saves the DO connection
    // cost (~$0.10/user/mo at scale). Free users get the same data
    // via the snapshot polls below, just at 60s cadence instead of
    // live + 30s.
    //
    // v0.31.9: openCloudStream mints a stream token from the
    // coordinator's /v1/stream/grant; local-daemon mode has no such
    // endpoint, so we don't try. Polling at tier cadence covers
    // the local-daemon Overview hydration.
    if (isCloudMode() && isSSEEnabled()) {
      openCloudStream();
    }
    // Polling cadence is now tier-aware (PR A). scheduleDynamic
    // re-reads the interval each cycle so mid-session upgrades flip
    // cadence on the next tick. Saved-cancel functions go on
    // cloudState so stop() can cancel them on page-deactivate.
    //
    // Cost math by tier:
    //   Free (solo):   60s × 2 endpoints = 2 req/min/user
    //   Team:          30s × 2 endpoints = 4 req/min/user
    //   Enterprise:    30s × 2 endpoints = 4 req/min/user
    // See docs/cost-model.md for the per-tier blended-margin math.
    cloudState.countsCancel = scheduleDynamic("overviewCounts", () => {
      fetchCountsOnce().then(() => {
        if (state.activePage === "overview") updateHero();
      });
    });
    cloudState.leasesCancel = scheduleDynamic("overviewLeases", () => {
      fetchLeasesOnce().then(() => {
        if (state.activePage === "overview") {
          renderOverviewTopChats();
          updateHero();
        }
      });
    });
  }
  // Tick-driven re-render. In local mode this is the only render
  // path. In cloud mode it backstops the fetch-driven renders so a
  // mid-tick state mutation from elsewhere still shows up.
  //
  // Also handles mid-session tier transitions (PR A): if SSE was off
  // because user was on Free, and they upgrade to Team mid-session,
  // open the stream on the next tick where isSSEEnabled() returns
  // true. The scheduleDynamic timers already self-update interval
  // each cycle, so they don't need explicit tier-transition logic
  // here.
  tickHandler = () => {
    if (state.activePage === "overview") renderOverview();
    // SSE upgrade/downgrade detection stays gated on isCloudMode()
    // specifically — the SSE flow uses a stream-token minted via
    // /v1/stream/grant against the coordinator. Local daemons don't
    // expose that endpoint (they would log directly to state via
    // /v1/events/stream); cloudState.running's poll loop already
    // covers local-daemon mode at tier-policy cadence.
    if (isCloudMode() && cloudState.running) {
      const sseShouldBeOn = isSSEEnabled();
      const sseIsOn = !!cloudState.es;
      if (sseShouldBeOn && !sseIsOn) {
        // Upgrade detected: open the stream.
        openCloudStream();
      } else if (!sseShouldBeOn && sseIsOn) {
        // Downgrade detected (e.g., subscription canceled, falls to
        // Free at period end): close the stream and lean on polls.
        // Also cancel any pending reconnect — otherwise scheduleCloudReconnect's
        // setTimeout will reopen the stream we just closed.
        closeCloudStream();
        if (cloudState.reconnectTimer) {
          clearTimeout(cloudState.reconnectTimer);
          cloudState.reconnectTimer = null;
        }
        // Bump streamGeneration so any in-flight openCloudStream
        // call from before this tick bails at its next await.
        cloudState.streamGeneration += 1;
      }
    }
  };
  document.addEventListener("dashboard:tick", tickHandler);

  // v0.38.0 Phase 4b: refetch leases immediately when the user
  // flips the mine-only toggle, instead of waiting up to 5s for
  // the next natural tick. Cheap (single API call) and the UX
  // payoff is the table visibly updating right after the click.
  document.addEventListener("dashboard:refetch-leases", () => {
    fetchLeasesOnce();
  });
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
  cloudState.running = false;
  state.overviewOwnsLeases = false;
  // PR A: dynamic scheduleDynamic timers return a cancel fn; legacy
  // setInterval timers used cloudState.countsTimer/leasesTimer. Keep
  // both teardown paths so a partial roll-forward (e.g. one timer
  // upgraded but not the other) doesn't leak.
  if (cloudState.countsCancel) {
    cloudState.countsCancel();
    cloudState.countsCancel = null;
  }
  if (cloudState.leasesCancel) {
    cloudState.leasesCancel();
    cloudState.leasesCancel = null;
  }
  if (cloudState.countsTimer) {
    clearInterval(cloudState.countsTimer);
    cloudState.countsTimer = null;
  }
  if (cloudState.leasesTimer) {
    clearInterval(cloudState.leasesTimer);
    cloudState.leasesTimer = null;
  }
  if (cloudState.reconnectTimer) {
    clearTimeout(cloudState.reconnectTimer);
    cloudState.reconnectTimer = null;
  }
  closeCloudStream();
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="overview"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
